// src/Services/ApiServices.js
import axios from 'axios'
import { useUserSettingsStore } from '@/store/userSettings'
import router from '@/router' // ⬅️ new

// Base URL from Vue CLI env
const baseURL = process.env.VUE_APP_ROOT_API || '/'

// ----------------------------------------
// Auth helper
// ----------------------------------------
export const auth = {
    // New JWT login — plain JSON, no form encoding
    async login(username, password) {
        const { data } = await axios.post(
            `${baseURL}/tzapp/login`,
            { username, password },
            { headers: { 'Content-Type': 'application/json' } }
        )

        // Expected response: { token, uid, name }
        // Return a normalized object like the old OAuth call did
        return {
            access_token: data.token,
            user_id: data.uid,
            name: data.name,
        }
    },

    // JWT flow has no refresh token (simpler)
    refresh() {
        console.warn('[auth.refresh] Not implemented for JWT (re-login instead)')
        return Promise.resolve(null)
    },
}

// ----------------------------------------
// Axios clients
// ----------------------------------------
export const api = axios.create({
    baseURL,
    withCredentials: false, // token-only auth; don't send cookies
    headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
    },
})

export const jsonApi = axios.create({
    baseURL,
    withCredentials: false, // important
    headers: {
        Accept: 'application/vnd.api+json, application/json',
        'Content-Type': 'application/vnd.api+json',
    },
})

// ----------------------------------------
// Interceptors: bearer + 401 handling
// ----------------------------------------
function attachBearer(instance) {
    instance.interceptors.request.use((config) => {
        try {
            const s = useUserSettingsStore()
            if (!config.headers) config.headers = {}

            const token = s?.accessToken
            if (token) {
                if (typeof config.headers.set === 'function') {
                    config.headers.set('Authorization', `Bearer ${token}`)
                } else {
                    config.headers['Authorization'] = `Bearer ${token}`
                }
            } else {
                if (typeof config.headers.delete === 'function') {
                    config.headers.delete('Authorization')
                } else {
                    delete config.headers.Authorization
                }
            }
        } catch (e) {
            console.warn('[jsonApi] attachBearer failed', e)
        }
        return config
    })
}

function attach401Handler(instance) {
    instance.interceptors.response.use(
        (response) => response,
        (error) => {
            const status = error?.response?.status

            if (status === 401) {
                const store = useUserSettingsStore()
                const current = router.currentRoute.value

                // Voorkom rare loops als we al op /settings staan
                if (current.name !== 'settings') {
                    // 🔴 Token is niet meer geldig → hard logout
                    store.logout()

                    router.push({
                        name: 'settings',
                        query: {
                            reason: 'expired',
                            redirect: current.fullPath,
                        },
                    })
                }
            }

            return Promise.reject(error)
        }
    )
}

attachBearer(api)
attachBearer(jsonApi)
attach401Handler(api)
attach401Handler(jsonApi)

// Dev logging voor jsonApi blijft zoals je had
if (process.env.NODE_ENV !== 'production') {
    jsonApi.interceptors.request.use((c) => {
        console.log(
            '[jsonApi] ->',
            c.method?.toUpperCase(),
            c.url,
            'Auth:',
            c.headers?.Authorization ? 'yes' : 'no'
        )
        return c
    })
    jsonApi.interceptors.response.use(
        (r) => {
            console.log('[jsonApi] <-', r.status, r.config.url)
            return r
        },
        (e) => {
            console.warn(
                '[jsonApi] ERR',
                e?.response?.status,
                e?.config?.url,
                e?.response?.data
            )
            return Promise.reject(e)
        }
    )
}

// ----------------------------------------
// Exported API surface
// ----------------------------------------
export default {
    // Public CSRF token (if you still need it)
    getCSRFToken() {
        return axios.get(`${baseURL}/session/token`)
    },

    // Example public/protected endpoints
    getNews() {
        return api.get('/news/titles')
    },

    // NEW: token-based endpoint (advisor1 OR advisor2 = current user)
    getMyProjects() {
        return api.get('/api/tzapp/projects/me')
    },

    // Legacy: keep during migration (requires uid in URL)
    getMyProjectsLegacy(uid) {
        return api.get(`/projects/${uid}/${uid}`)
    },

    // Reports (adjust when you add /me version)
    getReports(nid) {
        return api.get(`/api/tzapp/project/${nid}/reports`)
    },

    /**
     * Get a single project by its node ID
     */
    // TODO: replace /app_project/{nid} with /jsonapi/node/project/{uuid} later
    getProject(nid) {
        return api.get(`/api/tzapp/project/${nid}`)
    },

    // JSON:API node (example)
    getReport(uuid) {
        return jsonApi.get(`/jsonapi/node/inspection_report/${uuid}`)
    },
}
